import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, RefreshCw, Download, FileDown } from "lucide-react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/motion";
import { CONCEPTS } from "@/data/content";

const INDUSTRIES = ["Hospitality", "Education", "F&B", "Wellness", "Retail", "Technology", "Real Estate"];

const PALETTES = {
  "Sand & Ink": { bg: "#1A1713", accent: "#C5A059", soft: "#FDFBF7", swatches: ["#FDFBF7", "#C5A059", "#8a6d2f", "#1A1713"] },
  "Sage Calm": { bg: "#2E332B", accent: "#A9B99A", soft: "#EDEAE0", swatches: ["#EDEAE0", "#A9B99A", "#6C7A5C", "#2E332B"] },
  Ember: { bg: "#1A1210", accent: "#D8623F", soft: "#E8D8C0", swatches: ["#E8D8C0", "#D8623F", "#8C3520", "#1A1210"] },
  Azure: { bg: "#12233A", accent: "#4E86C6", soft: "#F2F4F7", swatches: ["#F2F4F7", "#4E86C6", "#2F5D8A", "#12233A"] },
  "Rose Noir": { bg: "#1E1416", accent: "#C77E8A", soft: "#F4ECEC", swatches: ["#F4ECEC", "#C77E8A", "#A64D5A", "#1E1416"] },
};

const VIBES = {
  Elegant: { headingClass: "font-serif italic tracking-tight", fonts: "Cormorant Garamond · DM Sans", taglines: ["Quietly unforgettable.", "Where {i} becomes belief.", "Refined, and it shows."] },
  Minimal: { headingClass: "font-sans uppercase tracking-[0.35em] text-4xl", fonts: "DM Sans · DM Sans", taglines: ["Less, but better.", "{b}. Nothing extra.", "Clarity as a strategy."] },
  Bold: { headingClass: "font-serif font-semibold tracking-tight", fonts: "Cormorant Garamond · DM Sans", taglines: ["Impossible to ignore.", "The obvious answer in {i}.", "Built to lead."] },
  Warm: { headingClass: "font-serif tracking-tight", fonts: "Cormorant Garamond · DM Sans", taglines: ["Made to be trusted.", "{i}, with a soul.", "Familiar, never forgettable."] },
  Modern: { headingClass: "font-sans font-semibold tracking-tight", fonts: "DM Sans · DM Sans", taglines: ["Ahead, on purpose.", "The new default in {i}.", "Designed forward."] },
};

const ConceptLab = () => {
  const [brand, setBrand] = useState("Solace");
  const [industry, setIndustry] = useState("Hospitality");
  const [palette, setPalette] = useState("Sand & Ink");
  const [vibe, setVibe] = useState("Elegant");
  const [tagIdx, setTagIdx] = useState(0);
  const previewRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const capture = async () => {
    const node = previewRef.current;
    return html2canvas(node, { scale: 2, backgroundColor: null, useCORS: true });
  };

  const fileName = (ext) => `${(brand || "concept").trim().replace(/\s+/g, "-").toLowerCase()}-aurelume-concept.${ext}`;

  const downloadPNG = async () => {
    setExporting(true);
    try {
      const canvas = await capture();
      const link = document.createElement("a");
      link.download = fileName("png");
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Concept image downloaded.");
    } catch (e) {
      toast.error("Could not export image.");
    }
    setExporting(false);
  };

  const downloadPDF = async () => {
    setExporting(true);
    try {
      const canvas = await capture();
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "px", format: [canvas.width, canvas.height] });
      pdf.addImage(img, "PNG", 0, 0, canvas.width, canvas.height);
      pdf.save(fileName("pdf"));
      toast.success("Concept PDF downloaded.");
    } catch (e) {
      toast.error("Could not export PDF.");
    }
    setExporting(false);
  };

  const p = PALETTES[palette];
  const v = VIBES[vibe];
  const initials = (brand.trim() || "AB").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const rawTag = v.taglines[tagIdx % v.taglines.length];
  const tagline = rawTag.replace("{b}", brand || "Your brand").replace("{i}", industry.toLowerCase());

  const chip = (active) =>
    `px-4 py-2 text-xs border transition-colors ${active ? "border-[#C5A059] bg-[#C5A059]/10 text-[#1A1713]" : "border-[#E8E4D9] text-[#78716C] hover:border-[#C5A059]"}`;

  return (
    <section className="py-24 md:py-32 bg-[#FDFBF7]" data-testid="concept-lab">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">— Concept Lab</p>
        <h2 className="font-serif font-light text-5xl md:text-6xl tracking-tight leading-none max-w-[16ch]">
          Test a concept for your requirements.
        </h2>
        <p className="text-base md:text-lg text-[#1A1713]/70 mt-6 max-w-xl leading-relaxed">
          A quick, playful sketch — not a final identity. Adjust the inputs and
          watch a direction take shape in real time.
        </p>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 mt-14 items-start">
          {/* Controls */}
          <div className="space-y-8">
            <div>
              <label className="block text-sm text-[#78716C] mb-2">Brand name</label>
              <input
                data-testid="lab-brand"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="w-full bg-white border border-[#E8E4D9] px-5 py-4 outline-none focus:border-[#C5A059] transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm text-[#78716C] mb-3">Industry</label>
              <div className="flex flex-wrap gap-2" data-testid="lab-industry">
                {INDUSTRIES.map((x) => (
                  <button key={x} onClick={() => setIndustry(x)} className={chip(industry === x)}>{x}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm text-[#78716C] mb-3">Personality</label>
              <div className="flex flex-wrap gap-2" data-testid="lab-vibe">
                {Object.keys(VIBES).map((x) => (
                  <button key={x} onClick={() => { setVibe(x); setTagIdx(0); }} className={chip(vibe === x)}>{x}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm text-[#78716C] mb-3">Palette</label>
              <div className="flex flex-wrap gap-2" data-testid="lab-palette">
                {Object.entries(PALETTES).map(([name, pal]) => (
                  <button key={name} onClick={() => setPalette(name)} className={`flex items-center gap-2 ${chip(palette === name)}`}>
                    <span className="flex">
                      {pal.swatches.map((s) => <span key={s} className="w-3 h-3" style={{ background: s }} />)}
                    </span>
                    {name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Live Preview */}
          <div className="lg:sticky lg:top-28" data-testid="lab-preview">
            <motion.div
              ref={previewRef}
              key={palette}
              initial={{ opacity: 0.6 }}
              animate={{ opacity: 1 }}
              className="aspect-[4/5] w-full flex flex-col justify-between p-10 md:p-14 relative overflow-hidden"
              style={{ background: p.bg, color: p.soft }}
            >
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <img src="/aurelume-mark.png" alt="" crossOrigin="anonymous" style={{ width: 22, height: 22, objectFit: "contain" }} />
                  <span className="font-serif text-sm tracking-[0.25em]" style={{ color: p.soft }}>AURELUME</span>
                </span>
                <span className="text-[10px] uppercase tracking-[0.3em]" style={{ color: p.accent }}>
                  {industry} · {vibe}
                </span>
              </div>

              <div className="flex flex-col items-center text-center">
                <div
                  className="w-24 h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center mb-8"
                  style={{ border: `1px solid ${p.accent}` }}
                >
                  <span className="font-serif text-4xl md:text-5xl" style={{ color: p.accent }}>{initials}</span>
                </div>
                <h3 className={`text-5xl md:text-6xl leading-none ${v.headingClass}`}>
                  {brand || "Your brand"}
                </h3>
                <p className="mt-5 text-lg md:text-xl opacity-80 font-serif italic">{tagline}</p>
              </div>

              <div>
                <div className="flex gap-2 mb-4">
                  {p.swatches.map((s) => <span key={s} className="h-7 flex-1" style={{ background: s }} />)}
                </div>
                <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] opacity-60">
                  <span>{v.fonts}</span>
                  <button
                    data-testid="lab-regenerate"
                    onClick={() => setTagIdx((i) => i + 1)}
                    className="inline-flex items-center gap-2 hover:opacity-100"
                    style={{ color: p.accent }}
                  >
                    <RefreshCw size={12} /> New line
                  </button>
                </div>
              </div>
            </motion.div>

            <div className="flex gap-3 mt-4">
              <button
                onClick={downloadPNG}
                disabled={exporting}
                data-testid="lab-download-png"
                className="group flex-1 inline-flex items-center justify-center gap-2 border border-[#1A1713] px-5 py-4 text-xs uppercase tracking-[0.18em] hover:bg-[#1A1713] hover:text-[#FDFBF7] transition-colors disabled:opacity-50"
              >
                <Download size={15} /> Download PNG
              </button>
              <button
                onClick={downloadPDF}
                disabled={exporting}
                data-testid="lab-download-pdf"
                className="group flex-1 inline-flex items-center justify-center gap-2 bg-[#C5A059] text-[#1A1713] px-5 py-4 text-xs uppercase tracking-[0.18em] hover:bg-[#1A1713] hover:text-[#FDFBF7] transition-colors disabled:opacity-50 font-medium"
              >
                <FileDown size={15} /> Download PDF
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Concepts() {
  const navigate = useNavigate();
  return (
    <main className="bg-[#FDFBF7] min-h-screen" data-testid="concepts-page">
      <Navbar dark />
      <section className="bg-[#1A1713] text-[#FDFBF7] pt-40 md:pt-52 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">— Concept Gallery</p>
          <h1 className="font-serif font-light text-5xl md:text-8xl tracking-tight leading-[0.92] max-w-[15ch]">
            Ideas we&rsquo;ve explored, not yet commissioned.
          </h1>
          <p className="text-base md:text-lg text-[#FDFBF7]/60 mt-8 max-w-xl leading-relaxed">
            Self-directed explorations — a way of showing how we think before a
            brief exists. None of these are client engagements.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-3 gap-8">
        {CONCEPTS.map((c, i) => (
          <Reveal key={c.slug} i={i}>
            <div className="group" data-testid={`concept-tile-${c.slug}`}>
              <div className="relative overflow-hidden h-[320px] mb-6">
                <img src={c.img} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <span className="inline-block text-[10px] uppercase tracking-[0.2em] text-[#8a6d2f] bg-[#C5A059]/15 px-3 py-1 rounded-full">{c.tag}</span>
              <h3 className="font-serif text-3xl mt-4">{c.title}</h3>
              <p className="text-sm text-[#78716C] mt-2 leading-relaxed">{c.desc}</p>
            </div>
          </Reveal>
        ))}
      </section>

      <ConceptLab />

      <section className="bg-[#26231F] text-[#FDFBF7] py-24 md:py-28">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <h3 className="font-serif font-light text-4xl md:text-6xl tracking-tight leading-none">
            Want the <em className="italic text-[#C5A059]">real</em> thing?
          </h3>
          <p className="text-base md:text-lg text-[#FDFBF7]/60 mt-6 max-w-xl mx-auto">
            Tell us about your establishment and we&rsquo;ll sketch a bespoke
            concept direction — free, no obligation.
          </p>
          <button
            onClick={() => navigate("/request-concept")}
            data-testid="concepts-request-cta"
            className="group mt-10 inline-flex items-center gap-3 bg-[#C5A059] text-[#1A1713] px-10 py-5 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#FDFBF7] transition-colors duration-400"
          >
            Request a concept for your brand
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </section>
      <Footer />
    </main>
  );
}
